import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCar,
  FaSearch,
  FaCalendarCheck,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaUserFriends,
  FaClock,
  FaCity,
  FaArrowLeft,
  FaTools,
  FaUser,
  FaCheckCircle,
  FaCarSide,
  FaPlus,
  FaTrash,
  FaRoute,
} from "react-icons/fa";
import config, { getUserCity } from "../../../config";
import { useCityPrice } from "../../../hooks/useCityPrice";

const MAX_VISITING_PLACES = 10;

const CarDriver = () => {
  const navigate = useNavigate();
  const { cityPrice } = useCityPrice();
  const [activeForm, setActiveForm] = useState("check");

  const [searchCriteria, setSearchCriteria] = useState({
    gender: "",
    careLocation: "",
    area: "",
  });

  const [scheduleData, setScheduleData] = useState({
	 name: "",                // NEW
    booking_mode: "",
    seat_capacity: "",
    car_model: "",
    pickup_date: "",
    pickup_slot: "",
    pickup_time: "",
    drop_date: "",
    drop_slot: "",
    drop_time: "",
    pickup_city: "",
    pickup_area: "",
    drop_city: "",
    drop_area: "",
    city: "",
    area: "",
    mobile: "",
    address: "",
    notes: "",
    visiting_places: [""],
  });

  const navigateBack = () => navigate(-1);

  const BASE_URL = config.API_BASE_URL || process.env.REACT_APP_API_BASE_URL;
  const categoryKey =
    config.TRANSPORT_SERVICE_CATEGORY || "transport-services";
  const serviceKey = config.CAR_DRIVER_SERVICE || "cardriver";
  const areaOptions = config.AREA_OPTIONS || {};

  const selectedCategory = config.SERVICE_CONFIG?.[categoryKey];
  const selectedService =
    config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey];

  const serviceTypes = Array.isArray(selectedService?.types)
    ? selectedService.types
    : [];

  const seatingModels = Array.isArray(selectedService?.seating_models)
    ? selectedService.seating_models
    : [];

  const seatOptions = seatingModels.map((item) => ({
    value: item.value,
    label: item.label,
  }));

  const filteredCarModels =
    seatingModels.find((item) => item.value === scheduleData.seat_capacity)
      ?.models || [];

  const selectedCarModelLabel =
    filteredCarModels.find(
      (model) => model.value === scheduleData.car_model
    )?.label || "";

  const selectedSeatLabel =
    seatOptions.find((seat) => seat.value === scheduleData.seat_capacity)
      ?.label || "";

  const serviceLabel = selectedService?.label || "Car Driver";
  const categoryLabel = selectedCategory?.label || "Transport Services";
  const scheduleDaysLimit = config.SCHEDULE_DAYS_LIMIT ?? 3;
  const slotStartHour = config.SLOT_START_HOUR ?? 6;
  const slotEndHour = config.SLOT_END_HOUR ?? 20;
  const slotDurationHours = config.SLOT_DURATION_HOURS ?? 2;

  const selectedModeConfig = serviceTypes.find(
    (item) => item.value === scheduleData.booking_mode
  );

  const selectedServiceCharge = selectedModeConfig
    ? cityPrice(selectedModeConfig.value, selectedModeConfig.price ?? 0)
    : null;

  const styles = {
    page: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f4f8ff 0%, #edf5ff 100%)",
      padding: "24px 0 40px",
      fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
    },
    container: {
      width: "98%",
      maxWidth: "1450px",
      margin: "0 auto",
    },
    hero: {
      background: "linear-gradient(135deg, #0f172a, #1d4ed8, #38bdf8)",
      color: "#fff",
      borderRadius: "26px",
      padding: "30px",
      boxShadow: "0 18px 42px rgba(29, 78, 216, 0.22)",
      marginBottom: "24px",
    },
    badge: {
      display: "inline-block",
      padding: "7px 14px",
      borderRadius: "999px",
      background: "rgba(152, 142, 142, 0.15)",
      fontSize: "13px",
      fontWeight: "700",
      marginBottom: "14px",
    },
    heroTitle: {
      margin: 0,
      fontSize: "2rem",
      fontWeight: "800",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
      color: "#fff",
    },
    heroSub: {
      marginTop: "10px",
      marginBottom: 0,
      color: "rgba(255,255,255,0.9)",
      fontSize: "1rem",
      maxWidth: "760px",
    },
    topBtn: {
      border: "none",
      borderRadius: "999px",
      padding: "11px 18px",
      fontWeight: "700",
      background: "#fff",
      color: "#1d4ed8",
      boxShadow: "0 8px 20px rgba(255,255,255,0.18)",
      cursor: "pointer",
    },
    summaryCard: {
      background: "#fff",
      borderRadius: "22px",
      padding: "22px",
      boxShadow: "0 10px 26px rgba(15, 23, 42, 0.08)",
      border: "1px solid #eaf0f8",
      height: "100%",
    },
    summaryTitle: {
      fontSize: "15px",
      fontWeight: "700",
      color: "#475569",
      marginBottom: "8px",
    },
    summaryValue: {
      fontSize: "1.9rem",
      fontWeight: "800",
      color: "#111827",
      marginBottom: "8px",
    },
    summaryText: {
      margin: 0,
      color: "#6b7280",
      fontSize: "14px",
    },
    formCard: {
      background: "#fff",
      borderRadius: "26px",
      boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
      border: "1px solid #ebf1f8",
      overflow: "hidden",
      maxWidth: "1350px",
      margin: "0 auto",
    },
    tabWrap: {
      display: "flex",
      gap: "12px",
      padding: "20px 20px 0 20px",
      flexWrap: "wrap",
    },
    tabBtn: (active) => ({
      border: "none",
      borderRadius: "999px",
      padding: "12px 20px",
      fontWeight: "700",
      background: active
        ? "linear-gradient(135deg, #1d4ed8, #2563eb)"
        : "#eef4ff",
      color: active ? "#fff" : "#1d4ed8",
      boxShadow: active ? "0 10px 24px rgba(37, 99, 235, 0.2)" : "none",
      cursor: "pointer",
    }),
    body: {
      padding: "32px",
    },
    sectionTitle: {
      fontSize: "1.5rem",
      fontWeight: "800",
      color: "#111827",
      marginBottom: "6px",
    },
    sectionSub: {
      color: "#6b7280",
      marginBottom: "20px",
      fontSize: "15px",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "700",
      color: "#334155",
      fontSize: "14px",
    },
    input: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      outline: "none",
      background: "#fff",
      fontSize: "15px",
      color: "#111827",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      outline: "none",
      background: "#fff",
      fontSize: "15px",
      color: "#111827",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      outline: "none",
      background: "#fff",
      fontSize: "15px",
      color: "#111827",
      boxSizing: "border-box",
      minHeight: "110px",
      resize: "vertical",
    },
    fieldCard: {
      background: "#f8fbff",
      border: "1px solid #e6eef8",
      borderRadius: "18px",
      padding: "18px",
      height: "100%",
      minWidth: 0,
    },
    iconBox: {
      width: "44px",
      height: "44px",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#eaf2ff",
      color: "#1d4ed8",
      marginBottom: "12px",
      fontSize: "18px",
    },
    submitBtn: {
      border: "none",
      background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
      color: "#fff",
      borderRadius: "999px",
      padding: "13px 22px",
      fontWeight: "800",
      minWidth: "180px",
      boxShadow: "0 10px 22px rgba(37,99,235,0.22)",
      cursor: "pointer",
    },
    infoBox: {
      background: "linear-gradient(135deg, #eff6ff, #f8fbff)",
      border: "1px solid #dbeafe",
      borderRadius: "18px",
      padding: "16px 18px",
      color: "#334155",
      fontSize: "14px",
      marginBottom: "22px",
    },
    priceBox: {
      background: "linear-gradient(135deg, #ecfdf5, #f0fdf4)",
      border: "1px solid #bbf7d0",
      borderRadius: "16px",
      padding: "16px 18px",
      marginTop: "20px",
      color: "#166534",
    },
    priceLabel: {
      fontSize: "14px",
      fontWeight: "700",
      marginBottom: "6px",
    },
    priceValue: {
      fontSize: "1.6rem",
      fontWeight: "800",
      margin: 0,
    },
    priceNote: {
      marginTop: "6px",
      marginBottom: 0,
      fontSize: "13px",
      color: "#15803d",
    },
    modeGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "18px",
      marginBottom: "24px",
    },
    modeCard: (active) => ({
      background: active ? "#eff6ff" : "#fff",
      border: active ? "2px solid #2563eb" : "1px solid #dbeafe",
      borderRadius: "20px",
      padding: "20px",
      cursor: "pointer",
      boxShadow: active ? "0 12px 24px rgba(37, 99, 235, 0.16)" : "none",
      transition: "all 0.2s ease",
      height: "100%",
    }),
    modeTitle: {
      fontSize: "1.2rem",
      fontWeight: "800",
      color: "#111827",
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    modeDesc: {
      color: "#64748b",
      fontSize: "14px",
      marginBottom: "12px",
      lineHeight: 1.6,
    },
    modePrice: {
      fontSize: "1.5rem",
      fontWeight: "900",
      color: "#1d4ed8",
      marginBottom: "10px",
    },
    modePoint: {
      fontSize: "14px",
      color: "#334155",
      marginBottom: "6px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    helperText: {
      marginTop: "8px",
      fontSize: "12px",
      color: "#64748b",
      lineHeight: 1.5,
    },
    addBtn: {
      border: "none",
      background: "#1d4ed8",
      color: "#fff",
      borderRadius: "999px",
      padding: "10px 16px",
      fontWeight: "700",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    removeBtn: {
      border: "none",
      background: "#ef4444",
      color: "#fff",
      borderRadius: "999px",
      padding: "10px 14px",
      fontWeight: "700",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    placeRow: {
      display: "flex",
      gap: "12px",
      alignItems: "center",
      marginBottom: "12px",
    },
  };

  const formatHourLabel = (hour) => {
    const suffix = hour >= 12 ? "pm" : "am";
    const normalized = hour % 12 === 0 ? 12 : hour % 12;
    return `${normalized}${suffix}`;
  };

  const timeSlots = useMemo(() => {
    const slots = [];
    let count = 1;

    for (
      let start = slotStartHour;
      start + slotDurationHours <= slotEndHour;
      start += slotDurationHours
    ) {
      const end = start + slotDurationHours;
      slots.push({
        slot: `slot${count}`,
        label: `${formatHourLabel(start)}-${formatHourLabel(end)}`,
      });
      count += 1;
    }

    return slots;
  }, [slotStartHour, slotEndHour, slotDurationHours]);

  const getCityAreas = (city) => {
    if (!city) return [];
    const cityKey = city.toLowerCase().trim();
    const cityConfig = areaOptions?.[cityKey];

    if (Array.isArray(cityConfig)) return cityConfig;
    if (Array.isArray(cityConfig?.areas)) return cityConfig.areas;

    return [];
  };

  const getCityLimit = (city) => {
    if (!city) return null;
    const cityKey = city.toLowerCase().trim();
    const cityConfig = areaOptions?.[cityKey];

    if (Array.isArray(cityConfig)) return null;
    return cityConfig?.limit ?? null;
  };

  const handleFormChangeCheckLabour = (e) => {
    const { name, value } = e.target;

    if (name === "careLocation") {
      setSearchCriteria((prev) => ({
        ...prev,
        careLocation: value,
        area: "",
      }));
      return;
    }

    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchCheckLabour = async (e) => {
    e.preventDefault();

    const { gender, careLocation, area } = searchCriteria;

    if (!gender || !careLocation || !area) {
      alert("Please fill all fields before searching");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/search/labour-otherservice/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...searchCriteria,
          service_category: categoryKey,
          service: serviceKey,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to check labour");
      }

      navigate("/labour-list", {
        state: {
          labours: data.labours || [],
          service_category: serviceLabel,
          service: serviceKey,
        },
      });
    } catch (error) {
      console.error("Request failed:", error);
      alert(error.message || "Request failed");
    }
  };

  const handleScheduleChange = (e) => {
    const { name, value } = e.target;

    if (name === "booking_mode") {
      setScheduleData((prev) => ({
        ...prev,
        booking_mode: value,
        seat_capacity: "",
        car_model: "",
      }));
      return;
    }

    if (name === "pickup_city") {
      setScheduleData((prev) => ({
        ...prev,
        pickup_city: value,
        pickup_area: "",
      }));
      return;
    }

    if (name === "drop_city") {
      setScheduleData((prev) => ({
        ...prev,
        drop_city: value,
        drop_area: "",
      }));
      return;
    }

    if (name === "city") {
      setScheduleData((prev) => ({
        ...prev,
        city: value,
        area: "",
      }));
      return;
    }

    setScheduleData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSeatCapacityChange = (e) => {
    const value = e.target.value;
    setScheduleData((prev) => ({
      ...prev,
      seat_capacity: value,
      car_model: "",
    }));
  };

  const handleSlotChange = (slotKey, timeKey) => (e) => {
    const selectedSlotKey = e.target.value;
    const selectedSlot = timeSlots.find((item) => item.slot === selectedSlotKey);

    setScheduleData((prev) => ({
      ...prev,
      [slotKey]: selectedSlot?.slot || "",
      [timeKey]: selectedSlot?.label || "",
    }));
  };

  const handlePlaceChange = (index, value) => {
    setScheduleData((prev) => {
      const updatedPlaces = [...prev.visiting_places];
      updatedPlaces[index] = value;
      return {
        ...prev,
        visiting_places: updatedPlaces,
      };
    });
  };

  const addVisitingPlace = () => {
    setScheduleData((prev) => {
      if (prev.visiting_places.length >= MAX_VISITING_PLACES) return prev;
      return {
        ...prev,
        visiting_places: [...prev.visiting_places, ""],
      };
    });
  };

  const removeVisitingPlace = (index) => {
    setScheduleData((prev) => {
      const updatedPlaces = prev.visiting_places.filter((_, i) => i !== index);
      return {
        ...prev,
        visiting_places: updatedPlaces.length ? updatedPlaces : [""],
      };
    });
  };

  const handleScheduleLabour = async (e) => {
    e.preventDefault();

    const {
      booking_mode,
      seat_capacity,
      car_model,
      pickup_date,
      pickup_slot,
      drop_date,
      drop_slot,
      pickup_city,
      pickup_area,
      drop_city,
      drop_area,
      mobile,
      address,
    } = scheduleData;

    if (
      !booking_mode ||
      !pickup_date ||
      !pickup_slot ||
      !drop_date ||
      !drop_slot ||
      !pickup_city ||
      !pickup_area ||
      !drop_city ||
      !drop_area ||
      !mobile ||
      !address
    ) {
      alert("Please fill all required schedule fields");
      return;
    }

    if (booking_mode === "driver_with_car" && !seat_capacity) {
      alert("Please select seat capacity");
      return;
    }

    if (booking_mode === "driver_with_car" && !car_model) {
      alert("Please select vehicle model");
      return;
    }

    const pickupDate = new Date(pickup_date);
    const dropDate = new Date(drop_date);
    const today = new Date();
    const maxDate = new Date();

    maxDate.setDate(maxDate.getDate() + scheduleDaysLimit);
    today.setHours(0, 0, 0, 0);
    maxDate.setHours(23, 59, 59, 999);

    if (pickupDate < today || pickupDate > maxDate) {
      alert(`Pickup date should be within today and next ${scheduleDaysLimit} days`);
      return;
    }

    if (dropDate < pickupDate) {
      alert("Drop date cannot be earlier than pickup date");
      return;
    }

    const cleanedVisitingPlaces = scheduleData.visiting_places
      .map((place) => place.trim())
      .filter(Boolean);

    try {
      let user = null;

      try {
        user = JSON.parse(localStorage.getItem("user"));
      } catch (error) {
        user = null;
      }

      const requestBody = {
        ...scheduleData,
        city: pickup_city,
        area: pickup_area,
        mobile: user?.mobile || scheduleData.mobile,
        service_category: categoryKey,
        selected_city: getUserCity(),
        service: serviceKey,
        service_type: booking_mode,
        service_charge: selectedServiceCharge,
        visiting_places: cleanedVisitingPlaces,
      };

      const response = await fetch(`${BASE_URL}/api/schedule/car_booking/labour/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to schedule labour");
      }

      alert(data.message || "Service scheduled successfully!");

      setScheduleData({
        booking_mode: "",
        seat_capacity: "",
        car_model: "",
        pickup_date: "",
        pickup_slot: "",
        pickup_time: "",
        drop_date: "",
        drop_slot: "",
        drop_time: "",
        pickup_city: "",
        pickup_area: "",
        drop_city: "",
        drop_area: "",
        city: "",
        area: "",
        mobile: "",
        address: "",
        notes: "",
        visiting_places: [""],
      });
    } catch (error) {
      console.error("Request failed:", error);
      alert(error.message || "Request failed");
    }
  };

  const getMinDate = () => new Date().toISOString().split("T")[0];

  const getMaxDate = () => {
    const max = new Date();
    max.setDate(max.getDate() + scheduleDaysLimit);
    return max.toISOString().split("T")[0];
  };

  const checkAreas = getCityAreas(searchCriteria.careLocation);
  const pickupAreas = getCityAreas(scheduleData.pickup_city);
  const dropAreas = getCityAreas(scheduleData.drop_city);
  const selectedCheckCityLimit = getCityLimit(searchCriteria.careLocation);
  const selectedPickupCityLimit = getCityLimit(scheduleData.pickup_city);
  const selectedDropCityLimit = getCityLimit(scheduleData.drop_city);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.hero}>
          <div className="row align-items-center g-3">
            <div className="col-lg-8">
              <span style={styles.badge}>Service Booking</span>
              <h2 style={styles.heroTitle}>
                <FaCar />
                {serviceLabel} Labour Services
              </h2>
              <p style={styles.heroSub}>
                Choose only driver or driver with vehicle, then fill pickup,
                drop, and visiting places before booking.
              </p>
            </div>

            <div className="col-lg-4 text-lg-end">
              <button style={styles.topBtn} onClick={navigateBack}>
                <FaArrowLeft className="me-2" />
                Back
              </button>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-md-4">
            <div style={styles.summaryCard}>
              <div style={styles.summaryTitle}>Service Category</div>
              <div style={styles.summaryValue}>{serviceLabel}</div>
              <p style={styles.summaryText}>
                Config-driven booking modes and trip details.
              </p>
            </div>
          </div>

          <div className="col-md-4">
            <div style={styles.summaryCard}>
              <div style={styles.summaryTitle}>Booking Modes</div>
              <div style={styles.summaryValue}>2</div>
              <p style={styles.summaryText}>
                Only Driver and Driver With Vehicle.
              </p>
            </div>
          </div>

          <div className="col-md-4">
            <div style={styles.summaryCard}>
              <div style={styles.summaryTitle}>Trip Stops</div>
              <div style={styles.summaryValue}>Up to 10</div>
              <p style={styles.summaryText}>
                Add visiting places one by one for the trip.
              </p>
            </div>
          </div>
        </div>

        <div style={styles.formCard}>
          <div style={styles.tabWrap}>
            <button
              type="button"
              style={styles.tabBtn(activeForm === "check")}
              onClick={() => setActiveForm("check")}
            >
              <FaSearch className="me-2" />
              Check Labour
            </button>

            <button
              type="button"
              style={styles.tabBtn(activeForm === "schedule")}
              onClick={() => setActiveForm("schedule")}
            >
              <FaCalendarCheck className="me-2" />
              Schedule Labour
            </button>
          </div>

          <div style={styles.body}>
            {activeForm === "check" ? (
              <form onSubmit={handleSearchCheckLabour}>
                <h3 style={styles.sectionTitle}>Check Labour Availability</h3>
                <p style={styles.sectionSub}>
                  Select preferred gender, city, and work area to check labour
                  availability.
                </p>

                <div style={styles.infoBox}>
                  Use the filters below to search driver availability by gender,
                  city, and working area.
                  {selectedCheckCityLimit !== null && searchCriteria.careLocation && (
                    <div className="mt-2">City limit: {selectedCheckCityLimit}</div>
                  )}
                </div>

                <div className="row g-4">
                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaUserFriends />
                      </div>
                      <label style={styles.label}>Gender</label>
                      <select
                        name="gender"
                        value={searchCriteria.gender}
                        onChange={handleFormChangeCheckLabour}
                        required
                        style={styles.select}
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaCity />
                      </div>
                      <label style={styles.label}>City</label>
                      <select
                        name="careLocation"
                        value={searchCriteria.careLocation}
                        onChange={handleFormChangeCheckLabour}
                        required
                        style={styles.select}
                      >
                        <option value="">Select City</option>
                        {Object.keys(areaOptions).map((city) => (
                          <option key={city} value={city}>
                            {city.charAt(0).toUpperCase() + city.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaMapMarkerAlt />
                      </div>
                      <label style={styles.label}>Working Area</label>
                      <select
                        name="area"
                        value={searchCriteria.area}
                        onChange={handleFormChangeCheckLabour}
                        required
                        style={styles.select}
                      >
                        <option value="">Select Area</option>
                        {checkAreas.map((area) => (
                          <option key={area} value={area}>
                            {area}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <button type="submit" style={styles.submitBtn}>
                    <FaSearch className="me-2" />
                    Search Labour
                  </button>
                </div>
              </form>
            ) : (
              <form
                onSubmit={handleScheduleLabour}
                style={{ maxWidth: "1250px", margin: "0 auto" }}
              >
                <h3 style={styles.sectionTitle}>Schedule Labour</h3>
                <p style={styles.sectionSub}>
                  Select booking mode, pickup and drop details, and add visiting
                  places if needed.
                </p>

                <div style={styles.infoBox}>
                  You can schedule only for today and the next {scheduleDaysLimit} days.
                  {selectedPickupCityLimit !== null && scheduleData.pickup_city && (
                    <div className="mt-2">
                      Pickup city limit: {selectedPickupCityLimit}
                    </div>
                  )}
                  {selectedDropCityLimit !== null && scheduleData.drop_city && (
                    <div className="mt-2">
                      Drop city limit: {selectedDropCityLimit}
                    </div>
                  )}
                </div>

                <div style={styles.modeGrid}>
                  <div
                    style={styles.modeCard(scheduleData.booking_mode === "only_driver")}
                    onClick={() =>
                      setScheduleData((prev) => ({
                        ...prev,
                        booking_mode: "only_driver",
                        seat_capacity: "",
                        car_model: "",
                      }))
                    }
                  >
                    <div style={styles.modeTitle}>
                      <FaUser />
                      Only Driver
                    </div>
                    <div style={styles.modeDesc}>
                      Book only a professional driver. Customer vehicle will be used.
                    </div>
                    <div style={styles.modePrice}>
                      ₹{" "}
                      {cityPrice("only_driver", serviceTypes.find((item) => item.value === "only_driver")?.price ?? 499)}
                    </div>
                    <div style={styles.modePoint}>
                      <FaCheckCircle /> Driver only
                    </div>
                    <div style={styles.modePoint}>
                      <FaCheckCircle /> Pickup city and area required
                    </div>
                    <div style={styles.modePoint}>
                      <FaCheckCircle /> Drop city and area required
                    </div>
                    <div style={styles.modePoint}>
                      <FaCheckCircle /> No vehicle selection needed
                    </div>
                  </div>

                  <div
                    style={styles.modeCard(
                      scheduleData.booking_mode === "driver_with_car"
                    )}
                    onClick={() =>
                      setScheduleData((prev) => ({
                        ...prev,
                        booking_mode: "driver_with_car",
                        seat_capacity: "",
                        car_model: "",
                      }))
                    }
                  >
                    <div style={styles.modeTitle}>
                      <FaCarSide />
                      Driver With Vehicle
                    </div>
                    <div style={styles.modeDesc}>
                      Book driver along with vehicle for your trip.
                    </div>
                    <div style={styles.modePrice}>
                      ₹{" "}
                      {cityPrice("driver_with_car", serviceTypes.find((item) => item.value === "driver_with_car")?.price ?? 1499)}
                    </div>
                    <div style={styles.modePoint}>
                      <FaCheckCircle /> Driver and vehicle included
                    </div>
                    <div style={styles.modePoint}>
                      <FaCheckCircle /> Seat capacity required
                    </div>
                    <div style={styles.modePoint}>
                      <FaCheckCircle /> Vehicle model required
                    </div>
                    <div style={styles.modePoint}>
                      <FaCheckCircle /> Pickup and drop details required
                    </div>
                  </div>
                </div>


                <div className="row g-4">
					{/* Customer Name */}
<div className="col-md-4">
  <div style={styles.fieldCard}>
    <div style={styles.iconBox}>
      <FaUser />
    </div>
    <label style={styles.label}>Name</label>
    <input
      type="text"
      name="name"
      value={scheduleData.name}
      onChange={handleScheduleChange}
      placeholder="Enter your name"
      required
      style={styles.input}
    />
  </div>
</div>

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaCalendarCheck />
                      </div>
                      <label style={styles.label}>Pickup Date</label>
                      <input
                        type="date"
                        name="pickup_date"
                        value={scheduleData.pickup_date}
                        min={getMinDate()}
                        max={getMaxDate()}
                        onChange={handleScheduleChange}
                        required
                        style={styles.input}
                      />
                    </div>
                  </div>

				  

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaClock />
                      </div>
                      <label style={styles.label}>Pickup Time Slot</label>
                      <select
                        name="pickup_slot"
                        value={scheduleData.pickup_slot}
                        onChange={handleSlotChange("pickup_slot", "pickup_time")}
                        required
                        style={styles.select}
                      >
                        <option value="">Select Pickup Time</option>
                        {timeSlots.map((item) => (
                          <option key={`pickup-${item.slot}`} value={item.slot}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaCalendarCheck />
                      </div>
                      <label style={styles.label}>Drop Date</label>
                      <input
                        type="date"
                        name="drop_date"
                        value={scheduleData.drop_date}
                        min={scheduleData.pickup_date || getMinDate()}
                        max={getMaxDate()}
                        onChange={handleScheduleChange}
                        required
                        style={styles.input}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaClock />
                      </div>
                      <label style={styles.label}>Drop Time Slot</label>
                      <select
                        name="drop_slot"
                        value={scheduleData.drop_slot}
                        onChange={handleSlotChange("drop_slot", "drop_time")}
                        required
                        style={styles.select}
                      >
                        <option value="">Select Drop Time</option>
                        {timeSlots.map((item) => (
                          <option key={`drop-${item.slot}`} value={item.slot}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {scheduleData.booking_mode === "driver_with_car" && (
                    <>
                      <div className="col-md-4">
                        <div style={styles.fieldCard}>
                          <div style={styles.iconBox}>
                            <FaCar />
                          </div>
                          <label style={styles.label}>Seat Capacity</label>
                          <select
                            name="seat_capacity"
                            value={scheduleData.seat_capacity}
                            onChange={handleSeatCapacityChange}
                            required
                            style={styles.select}
                          >
                            <option value="">Select Seat Capacity</option>
                            {seatOptions.map((seat) => (
                              <option key={seat.value} value={seat.value}>
                                {seat.label}
                              </option>
                            ))}
                          </select>
                          <div style={styles.helperText}>
                            Select seat capacity first.
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div style={styles.fieldCard}>
                          <div style={styles.iconBox}>
                            <FaCarSide />
                          </div>
                          <label style={styles.label}>Vehicle Model</label>
                          <select
                            name="car_model"
                            value={scheduleData.car_model}
                            onChange={handleScheduleChange}
                            required
                            disabled={!scheduleData.seat_capacity}
                            style={{
                              ...styles.select,
                              background: scheduleData.seat_capacity ? "#fff" : "#f1f5f9",
                              cursor: scheduleData.seat_capacity
                                ? "pointer"
                                : "not-allowed",
                            }}
                          >
                            <option value="">
                              {scheduleData.seat_capacity
                                ? "Select Vehicle Model"
                                : "Select Seat Capacity First"}
                            </option>
                            {filteredCarModels.map((model) => (
                              <option key={model.value} value={model.value}>
                                {model.label}
                              </option>
                            ))}
                          </select>
                          <div style={styles.helperText}>
                            Models appear based on selected seat capacity.
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaCity />
                      </div>
                      <label style={styles.label}>Pickup City</label>
                      <select
                        name="pickup_city"
                        value={scheduleData.pickup_city}
                        onChange={handleScheduleChange}
                        required
                        style={styles.select}
                      >
                        <option value="">Select Pickup City</option>
                        {Object.keys(areaOptions).map((city) => (
                          <option key={city} value={city}>
                            {city.charAt(0).toUpperCase() + city.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaMapMarkerAlt />
                      </div>
                      <label style={styles.label}>Pickup Area</label>
                      <select
                        name="pickup_area"
                        value={scheduleData.pickup_area}
                        onChange={handleScheduleChange}
                        required
                        style={styles.select}
                      >
                        <option value="">Select Pickup Area</option>
                        {pickupAreas.map((area) => (
                          <option key={area} value={area}>
                            {area}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaCity />
                      </div>
                      <label style={styles.label}>Drop City</label>
                      <select
                        name="drop_city"
                        value={scheduleData.drop_city}
                        onChange={handleScheduleChange}
                        required
                        style={styles.select}
                      >
                        <option value="">Select Drop City</option>
                        {Object.keys(areaOptions).map((city) => (
                          <option key={city} value={city}>
                            {city.charAt(0).toUpperCase() + city.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaMapMarkerAlt />
                      </div>
                      <label style={styles.label}>Drop Area</label>
                      <select
                        name="drop_area"
                        value={scheduleData.drop_area}
                        onChange={handleScheduleChange}
                        required
                        style={styles.select}
                      >
                        <option value="">Select Drop Area</option>
                        {dropAreas.map((area) => (
                          <option key={area} value={area}>
                            {area}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaPhoneAlt />
                      </div>
                      <label style={styles.label}>Mobile Number</label>
                      <input
                        type="tel"
                        name="mobile"
                        value={scheduleData.mobile}
                        onChange={handleScheduleChange}
                        placeholder="Enter mobile number"
                        pattern="[0-9]{10}"
                        minLength={10}
                        maxLength={10}
                        required
                        style={styles.input}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaMapMarkerAlt />
                      </div>
                      <label style={styles.label}>Pickup / Address</label>
                      <input
                        type="text"
                        name="address"
                        value={scheduleData.address}
                        onChange={handleScheduleChange}
                        placeholder="Enter exact address"
                        required
                        style={styles.input}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaTools />
                      </div>
                      <label style={styles.label}>Notes</label>
                      <textarea
                        name="notes"
                        value={scheduleData.notes}
                        onChange={handleScheduleChange}
                        placeholder="Extra travel details, luggage, trip preferences, etc."
                        style={styles.textarea}
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <div style={styles.fieldCard}>
                      <div style={styles.iconBox}>
                        <FaRoute />
                      </div>
                      <label style={styles.label}>Visiting Places (Up to 10)</label>

                      {scheduleData.visiting_places.map((place, index) => (
                        <div key={index} style={styles.placeRow}>
                          <input
                            type="text"
                            value={place}
                            onChange={(e) => handlePlaceChange(index, e.target.value)}
                            placeholder={`Visiting Place ${index + 1}`}
                            style={styles.input}
                          />
                          {scheduleData.visiting_places.length > 1 && (
                            <button
                              type="button"
                              style={styles.removeBtn}
                              onClick={() => removeVisitingPlace(index)}
                            >
                              <FaTrash />
                              Remove
                            </button>
                          )}
                        </div>
                      ))}

                      <div className="d-flex flex-wrap gap-2 mt-2">
                        <button
                          type="button"
                          style={styles.addBtn}
                          onClick={addVisitingPlace}
                          disabled={
                            scheduleData.visiting_places.length >= MAX_VISITING_PLACES
                          }
                        >
                          <FaPlus />
                          Add Place
                        </button>
                      </div>

                      <div style={styles.helperText}>
                        Add places one by one. Maximum {MAX_VISITING_PLACES} places.
                      </div>
                    </div>
                  </div>
                </div>

                {scheduleData.booking_mode && (
                  <div style={styles.priceBox}>
                    <div style={styles.priceLabel}>Booking Summary</div>
                    <p style={{ ...styles.priceValue, color: "#000" }}>
                      ₹ {selectedServiceCharge || 0}
                    </p>

                    <p style={styles.priceNote}>
                      Mode:{" "}
                      {scheduleData.booking_mode === "only_driver"
                        ? "Only Driver"
                        : "Driver With Vehicle"}
                    </p>

                    {scheduleData.seat_capacity && (
                      <p style={{ margin: "6px 0 0", color: "#000" }}>
                        Seat Capacity: {selectedSeatLabel || scheduleData.seat_capacity}
                      </p>
                    )}

                    {scheduleData.car_model && (
                      <p style={{ margin: "6px 0 0", color: "#000" }}>
                        Vehicle Model: {selectedCarModelLabel}
                      </p>
                    )}

                    {scheduleData.pickup_city && scheduleData.pickup_area && (
                      <p style={{ margin: "6px 0 0", color: "#000" }}>
                        Pickup: {scheduleData.pickup_city} | {scheduleData.pickup_area}
                      </p>
                    )}

                    {scheduleData.drop_city && scheduleData.drop_area && (
                      <p style={{ margin: "6px 0 0", color: "#000" }}>
                        Drop: {scheduleData.drop_city} | {scheduleData.drop_area}
                      </p>
                    )}

                    {scheduleData.pickup_date && scheduleData.pickup_time && (
                      <p style={{ margin: "6px 0 0", color: "#000" }}>
                        Pickup Time: {scheduleData.pickup_date} | {scheduleData.pickup_time}
                      </p>
                    )}

                    {scheduleData.drop_date && scheduleData.drop_time && (
                      <p style={{ margin: "6px 0 0", color: "#000" }}>
                        Drop Time: {scheduleData.drop_date} | {scheduleData.drop_time}
                      </p>
                    )}

                    {scheduleData.visiting_places.some((place) => place.trim()) && (
                      <p style={{ margin: "6px 0 0", color: "#000" }}>
                        Visiting Places:{" "}
                        {scheduleData.visiting_places
                          .filter((place) => place.trim())
                          .join(", ")}
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-4">
                  <button type="submit" style={styles.submitBtn}>
                    <FaCalendarCheck className="me-2" />
                    Schedule Service
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CarDriver;